import { AxiosResponse } from 'axios'
import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import { OrderShipmentLookupsDao, OrderShipmentRowDao } from '@/interfaces/order/OrderShipment.dao'
import OrderShipmentDetailDao, {
    BulkPrintOptionsDao,
    BulkPrintResultDao,
    CarrierLabelResultDao,
    PrintCarrierLabelDao,
    ShipOrderLineDao,
    ShipOrderResultDao,
} from '@/interfaces/order/OrderShipmentDetail.dao'

export default class OrderShipmentsService extends ApiService {
    async searchOrderShipments(
        params?: IRequestParameters,
    ): Promise<[OrderShipmentRowDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrderShipmentRowDao[], PageNumberPagination] | null>(
            'GET',
            '/order-shipments',
            params,
            undefined,
            undefined,
            'searchOrderShipments',
        ).catch(() => null)
    }

    async getOrderShipmentLookups(): Promise<OrderShipmentLookupsDao | null> {
        return this.request<undefined, OrderShipmentLookupsDao>(
            'GET',
            '/order-shipments/lookups',
            undefined,
            undefined,
            undefined,
            'getOrderShipmentLookups',
        ).catch(() => null)
    }

    async getOrderShipment(id: number): Promise<OrderShipmentDetailDao | null> {
        return this.request<undefined, OrderShipmentDetailDao>(
            'GET',
            `/order-shipments/${id}`,
            undefined,
            undefined,
            undefined,
            'getOrderShipment',
        ).catch(() => null)
    }

    async shipOrder(id: number, lines: ShipOrderLineDao[]): Promise<ShipOrderResultDao | null> {
        return this.request<{ Lines: ShipOrderLineDao[] }, ShipOrderResultDao>(
            'POST',
            `/order-shipments/${id}/ship`,
            undefined,
            { Lines: lines },
            undefined,
            'shipOrder',
        ).catch(() => null)
    }

    async exportOrderShipments(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/order-shipments/export',
            params,
            undefined,
            'blob',
            'exportOrderShipments',
        ).catch(() => null)
    }

    async generatePickList(params: IRequestParameters, orderIds?: number[]): Promise<AxiosResponse | null> {
        return this.request<{ orderIds?: number[] }, AxiosResponse>(
            'POST',
            '/order-shipments/pick-list',
            params,
            { orderIds },
            'blob',
            'generatePickList',
        ).catch(() => null)
    }

    async printPackingSlip(id: number): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'GET',
            `/order-shipments/${id}/documents/packing-slip`,
            undefined,
            undefined,
            'blob',
            'printPackingSlip',
        ).catch(() => null)
    }

    async printAddressLabel(id: number): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'GET',
            `/order-shipments/${id}/documents/address-label`,
            undefined,
            undefined,
            'blob',
            'printAddressLabel',
        ).catch(() => null)
    }

    async printCarrierLabel(id: number, body: PrintCarrierLabelDao): Promise<CarrierLabelResultDao | null> {
        return this.request<PrintCarrierLabelDao, CarrierLabelResultDao>(
            'POST',
            `/order-shipments/${id}/carrier-label`,
            undefined,
            body,
            undefined,
            'printCarrierLabel',
        ).catch(() => null)
    }

    async downloadCarrierLabel(trackingNumberId: number): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'GET',
            `/order-shipments/tracking-numbers/${trackingNumberId}/carrier-label`,
            undefined,
            undefined,
            'blob',
            'downloadCarrierLabel',
        ).catch(() => null)
    }

    async bulkPrintAndShip(
        options: BulkPrintOptionsDao,
        params?: IRequestParameters,
    ): Promise<BulkPrintResultDao | null> {
        return this.request<BulkPrintOptionsDao, BulkPrintResultDao>(
            'POST',
            '/order-shipments/bulk-print-and-ship',
            params,
            options,
            undefined,
            'bulkPrintAndShip',
        ).catch(() => null)
    }

    async downloadBulkPrintFile(file: string): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'GET',
            '/order-shipments/bulk-print-files',
            { file },
            undefined,
            'blob',
            'downloadBulkPrintFile',
        ).catch(() => null)
    }
}
